import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { Currency } from '@/generated/gql/graphql';
import { termsFormDataDefault } from '~/components/NegotiationTermsForm/NegotiationTermsForm.schema';
import {
  supplierContactFormDataDefault,
  supplierContactFormDataSchema,
} from '~/modules/NegotiationSupplierContactWidget/components/SupplierContactForm/SupplierContactForm.schema';

export const quoteFormDataSchema = z.object({
  id: z.string().nullable(),
  currency: z.string(),
  quoteReference: z.string().trim().min(1),
  summary: z.string().nullable(),
  lineItems: z.array(
    z.object({
      id: z.string().nullable(),
      position: z.string().min(1).nullable(),
      title: z.string().min(1),
      description: z.string().nullable(),
      commodityGroupId: z.string().nullable(),
      unitId: z.string().min(1),
      quantity: z.number().refine((val) => val > 0),
      pricePerUnit: z.number().min(0),
      expectedDeliveryFrom: z.date().nullable(),
      expectedDeliveryTo: z.date().nullable(),
    }),
  ),
});

export const refineQuoteDetailsStepFormDataSchema = quoteFormDataSchema.extend({
  supplier: supplierContactFormDataSchema.shape.supplier.extend({
    contact: supplierContactFormDataSchema.shape.supplier.shape.contact.nullish(),
    location: supplierContactFormDataSchema.shape.supplier.shape.location.nullish(),
  }),
});

export type RefineQuoteDetailsStepFormData = z.infer<typeof refineQuoteDetailsStepFormDataSchema>;

export const refineQuoteDetailsStepFormDataDefault: DefaultValues<RefineQuoteDetailsStepFormData> = {
  ...supplierContactFormDataDefault,
  ...termsFormDataDefault,
  currency: Currency.EUR,
  id: null,
  lineItems: [],
  quoteReference: '',
  summary: null,
};

export const SvbrgQuoteToFormStateMapper = z.looseObject({
  lineItems: z.array(
    z
      .looseObject({
        quantity: z
          .string()
          .nullish()
          .transform((val) => Number(val)),
        pricePerUnit: z
          .string()
          .nullish()
          .transform((val) => Number(val)),
        commodityGroupId: z
          .string()
          .nullish()
          .transform((val) => val ?? null), // TODO: Check whether this should come from 7bridges already
      })
      .transform(({ earlyDeliveryAt, lateDeliveryAt, ...rest }) => ({
        ...rest,
        expectedDeliveryFrom: earlyDeliveryAt ? new Date(earlyDeliveryAt as string) : null,
        expectedDeliveryTo: lateDeliveryAt ? new Date(lateDeliveryAt as string) : null,
      })),
  ),
});

export const toWizardStateMapper = refineQuoteDetailsStepFormDataSchema.transform(({ supplier, ...quote }) => ({
  supplier: {
    ...supplier,
    contact: supplier.contact ?? undefined,
    location: supplier.location ?? undefined,
  },
  quote: {
    ...quote,
    supplierId: supplier.internalId,
    lineItems: quote.lineItems.map(
      ({ expectedDeliveryFrom: earlyDeliveryAt, expectedDeliveryTo: lateDeliveryAt, ...rest }) => ({
        ...rest,
        earlyDeliveryAt,
        lateDeliveryAt,
      }),
    ),
  },
}));
